import { useTheme } from 'styled-components';

import { ProgressIndicator } from '@speeki/client-admin';
import { Typography } from '@speeki/global-ui-components';
import { IconManager, IconManagerProps } from '@speeki/icons';

import { CardLink } from '@components/atoms/CardLink/CardLink';

import { CategoryName } from '@utils/esgCategory';

import {
    LinkWrapper,
    QuestionnaireStatisticContent,
    QuestionnaireStatisticHeader,
    QuestionnaireStatisticInfo,
    QuestionnaireStatisticsIcon,
    QuestionnaireStatisticWrapper,
} from './QuestionnaireStatistic.styled';

export interface QuestionnaireStatisticProps {
    categoryName: CategoryName;
    link: string;
    loading: boolean;
    progressIndicator: {
        backgroundColor: string;
        progressColor: string;
    };
    statistics: {
        allQuestions: number;
        filledQuestions: number;
    };
    title: string;
}

const statisticIcons: Partial<Record<CategoryName, IconManagerProps['icon']>> =
    {
        environmental: 'SvgEnvironmental24',
        governance: 'SvgGovernance24',
        social: 'SvgSocial24',
    };

const statisticIconColorsByCategory: Partial<Record<CategoryName, string>> = {
    environmental: '#21C45D',
    governance: '#AD5CD6',
    social: '#1AA2E6',
};

const fallbackStatisticIcon: IconManagerProps['icon'] = 'SvgEnvironmental24';
const fallbackStatisticIconColor = '#21C45D';

export const QuestionnaireStatistic = ({
    categoryName,
    link,
    progressIndicator,
    statistics,
    title,
}: QuestionnaireStatisticProps) => {
    const { palette } = useTheme();

    const iconBackgroundColor =
        palette.esg[`${categoryName}Background`] ?? palette.neutral.gray;

    const icon = statisticIcons[categoryName] ?? fallbackStatisticIcon;

    const iconColor =
        statisticIconColorsByCategory[categoryName] ??
        fallbackStatisticIconColor;

    const percentFilled = Math.floor(
        (statistics.filledQuestions / statistics.allQuestions) * 100,
    );

    return (
        <QuestionnaireStatisticWrapper>
            <QuestionnaireStatisticsIcon backgroundColor={iconBackgroundColor}>
                <IconManager
                    customColors={{
                        default: {
                            'color-1': iconColor,
                        },
                    }}
                    icon={icon}
                    size={16}
                />
            </QuestionnaireStatisticsIcon>
            <div>
                <QuestionnaireStatisticHeader>
                    <Typography.TextBody_14_24 fontWeight="bold">
                        {title}
                    </Typography.TextBody_14_24>
                    {statistics.filledQuestions === 0 ? (
                        <LinkWrapper>
                            <CardLink href={link}>Start</CardLink>
                        </LinkWrapper>
                    ) : (
                        <QuestionnaireStatisticInfo>
                            {statistics.filledQuestions}/
                            {statistics.allQuestions} (
                            {parseFloat(percentFilled.toString()).toFixed()}%)
                        </QuestionnaireStatisticInfo>
                    )}
                </QuestionnaireStatisticHeader>
                <QuestionnaireStatisticContent>
                    <ProgressIndicator
                        backgroundColor={progressIndicator.backgroundColor}
                        progressColor={progressIndicator.progressColor}
                        progressValue={percentFilled}
                    />
                </QuestionnaireStatisticContent>
            </div>
        </QuestionnaireStatisticWrapper>
    );
};
